import { Tooltip } from '@material-ui/core';
import { Bookmark, BookmarkBorderOutlined } from '@material-ui/icons';
import { Color, ColorDynamic, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { DisabledBookmark } from 'shared/icons/DisabledBookmark';
import styled from 'styled-components';
import { isPostingUnposted, PostingDTO } from '../../data/dto/PostingDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import {
  useDeleteSavedLoadMutation,
  useSaveLoadMutation,
} from '../../data/LoadboardSavedLoadsAPI';
import { trackLoadboardEvent } from '../LoadboardAnalytics';

const SavedLoadIcon = styled(Bookmark)<{ $size?: string }>`
  font-size: ${(props) => (props.$size === 'small' ? '20px' : '24px')};
  cursor: pointer;
  color: ${ColorDynamic.Blue500};
`;

const NotSavedLoadIcon = styled(BookmarkBorderOutlined)<{ $size?: string }>`
  font-size: ${(props) => (props.$size === 'small' ? '20px' : '24px')};
  cursor: pointer;
`;

const DisabledSavedLoadIcon = styled(DisabledBookmark)<{ $size?: string }>`
  font-size: ${(props) => (props.$size === 'small' ? '20px' : '24px')};
  cursor: pointer;
`;

interface Props {
  size?: 'small' | 'medium';
  posting: PostingDTO;
}

export function SavedLoadStatus({ size, posting }: Props) {
  const { addSnackbar } = useSnackbarStack();
  const { router } = useLoadboardContext();

  const saveLoadMutation = useSaveLoadMutation({
    onSuccess: () => {
      trackLoadboardEvent({ name: 'SLB: Saved Load', load: posting });
      addSnackbar('Load saved', {
        variant: 'success',
        action: (
          <Button
            variant="inverted"
            onClick={() => {
              router.openPostingsScreen('saved-loads');
            }}
          >
            View Saved
          </Button>
        ),
      });
    },
    onError: (error) => {
      addSnackbar(error.message || 'Failed to save load');
    },
  });

  const deleteLoadMutation = useDeleteSavedLoadMutation({
    onSuccess: () => {
      addSnackbar('Load removed from Saved', { variant: 'success' });
    },
    onError: (error) => {
      addSnackbar(error.message || 'Failed to delete saved load', {
        variant: 'error',
      });
    },
  });

  if (posting.is_saved) {
    return (
      <SavedLoadIcon
        $size={size}
        color="primary"
        aria-label="Remove from Saved"
        onClick={(event) => {
          deleteLoadMutation.mutate(posting.load.guid);
          event.stopPropagation();
        }}
      />
    );
  }

  if (isPostingUnposted(posting)) {
    return (
      <Tooltip title="Unposted or booked loads cannot be saved">
        <DisabledSavedLoadIcon
          $size={size}
          htmlColor={ColorDynamic.Silver500}
        />
      </Tooltip>
    );
  }

  return (
    <NotSavedLoadIcon
      $size={size}
      htmlColor={Color.Dark100}
      aria-label="Save Load"
      data-intercom-target="Save Load Button"
      onClick={(event) => {
        saveLoadMutation.mutate(posting.load.guid);
        event.stopPropagation();
      }}
    />
  );
}
